import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowsClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowsClockwise';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { Button } from '../../design-system/primitives/Button/Button';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import { smsApi, type SmsMessageRow } from '../../lib/api/sms';
import styles from './SendMessageDialog.module.css';

const FORBIDDEN_CODE = 'forbidden';

/**
 * Resend the case to the patient — `POST /cases/{case}/resend`. The API
 * picks the pharmacy-visit message the case's product lines call for and
 * fills it with the codes the case already holds, so there is nothing to
 * choose here: the dialog only confirms, because the patient's phone
 * receives it.
 */
export interface ResendCaseDialogProps {
  open: boolean;
  caseUuid: string;
  onClose: () => void;
  onSent: (row: SmsMessageRow) => void;
}

export function ResendCaseDialog({ open, caseUuid, onClose, onSent }: ResendCaseDialogProps) {
  const { t } = useTranslation();

  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const inFlight = useRef(false);

  useEffect(() => {
    if (open) setFailure(null);
  }, [open]);

  function describeFailure(error: ApiError): string {
    // A field message (a template variable the case has no value for) is
    // the API's own sentence, already in the reader's language.
    const detail = Object.values(error.details)[0]?.[0];
    if (detail !== undefined && error.status !== 409) return detail;

    if (error.status === 409) return t('sms.resendClosed');

    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('cases.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('cases.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('cases.actionFailed');
    }
  }

  async function resend(): Promise<void> {
    if (inFlight.current) return;

    inFlight.current = true;
    setSubmitting(true);
    setFailure(null);

    try {
      onSent(await smsApi.resendCase(caseUuid));
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setFailure(describeFailure(error));
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} kind="form" onClose={onClose} title={t('sms.resendTitle')}>
      <div className={styles['form']}>
        <div className={styles['failure']} role="alert" aria-atomic="true">
          {failure}
        </div>

        <p>{t('sms.resendIntro')}</p>

        <div className={styles['actions']}>
          <Button
            type="button"
            loading={submitting}
            onClick={() => {
              void resend();
            }}
            icon={<ArrowsClockwiseIcon size={16} />}
          >
            {t('sms.resendConfirm')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} icon={<XIcon size={16} />}>
            {t('cases.cancel')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
